import { Typography } from '@euler/app/components/typography';
import { Panel } from '@euler/app/tv/components/Panel';
import type { SearchCriteriaNavParams } from '@euler/app/tv/components/search/SearchCriteriaContainer';
import { getSearchCriterionProvider } from '@euler/app/tv/functions/search-criteria/factory';
import {
  SearchCriterionOption,
  SearchCriterionType,
  SearchCriterionValueType,
} from '@euler/app/tv/functions/search-criteria/types';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useLocalObservable } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useMemo } from 'react';
import {
  FlatList,
  ListRenderItem,
  StyleProp,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { ImageStyle } from 'react-native-fast-image';

export type CriterionOptionsSelectorProps<
  T extends SearchCriterionType,
  U = SearchCriterionValueType<T>,
> = {
  criterionType: T;
  selectedValue: U | undefined;
  onPress?: (value: U | undefined) => void;
};

export const SearchCriterionOptionSelectorScreen = wrapNavigatorScreen(
  <T extends SearchCriterionType, U = SearchCriterionValueType<T>>({
    criterionType,
    selectedValue,
    onPress,
  }: CriterionOptionsSelectorProps<T, U>) => {
    const { rpx } = useResponsiveContext();

    const navigation =
      useNavigation<StackNavigationProp<SearchCriteriaNavParams>>();

    const provider = useMemo(
      () => getSearchCriterionProvider<T, U>(criterionType),
      [criterionType],
    );
    const options = useLocalObservable(provider.getOptions(), []);

    const renderItem: ListRenderItem<SearchCriterionOption<U>> = useCallback(
      ({ item, index }) => {
        return (
          <Cell
            navigation={navigation}
            selected={provider.equals(item.value, selectedValue)}
            text={item.text ?? provider.getDisplayText(item.value)}
            value={item.value}
            first={index === 0}
            showImage={item.showImage}
            image={item.image}
            imageStyle={item.imageStyle}
            style={[item.style]}
            onPress={onPress as any}
          />
        );
      },
      [navigation, onPress, provider, selectedValue],
    );

    return (
      <Panel
        title={provider.getLabel()}
        beforeTitle={<BackButton />}
        contentContainerStyle={{
          flex: 1,
          paddingTop: 0,
          paddingLeft: 0,
          paddingBottom: 0,
          paddingRight: 0,
        }}
        headerStyle={{
          marginTop: rpx(20),
          marginLeft: 0,
          marginBottom: rpx(20),
        }}
        titleStyle={{
          fontSize: rpx(28),
          lineHeight: rpx(44),
          marginLeft: rpx(4),
        }}
        css={`
          flex: 1;
          overflow: hidden;
        `}
      >
        <FlatList
          data={options as any}
          keyExtractor={x => provider.getKey(x.value as any)}
          renderItem={renderItem}
          contentContainerStyle={{
            paddingBottom: rpx(16),
          }}
        />
      </Panel>
    );
  },
);

const Cell = memo(
  <T extends SearchCriterionType, U = SearchCriterionValueType<T>>({
    value,
    text,
    style,
    first,
    selected,
    showImage,
    image,
    imageStyle,
    onPress,
  }: {
    value: U | undefined;
    text: string;
    style?: StyleProp<ViewStyle>;
    first?: boolean;
    showImage?: boolean;
    image?: string;
    imageStyle?: StyleProp<ImageStyle>;
    navigation: StackNavigationProp<SearchCriteriaNavParams>;
    selected?: boolean;
    onPress?: (value: U | undefined) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const handlePress = useCallback(() => {
      onPress?.(value);
    }, [onPress, value]);

    const {
      search: {
        criterion: {
          optionListItem: {
            titleTextColor,
            checkmarkIconColor,
            separatorColor,
          },
        },
      },
    } = theme;

    return (
      <TouchableOpacity
        css={`
          border-top-width: ${first ? 0 : 1}px;
          border-top-color: ${separatorColor};
          padding: ${rpx(16)}px ${rpx(32)}px;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
        style={style}
        onPress={handlePress}
      >
        <HStack
          css={`
            flex: 1;
            justify-content: flex-start;
          `}
        >
          {showImage ? (
            <Img
              uri={image}
              css={`
                width: ${rpx(32)}px;
                height: ${rpx(32)}px;
                margin-right: ${rpx(16)}px;
              `}
              style={imageStyle}
            />
          ) : null}
          <Typography.Label
            regular
            color={titleTextColor}
            size={rpx(22)}
            lineHeight={rpx(26)}
          >
            {text}
          </Typography.Label>
        </HStack>
        {selected ? (
          <Icon name="check" color={checkmarkIconColor} size={rpx(28)} />
        ) : null}
      </TouchableOpacity>
    );
  },
);

const BackButton = memo(({ style }: { style?: StyleProp<ViewStyle> }) => {
  const navigation = useNavigation();
  const theme = usePresentationTheme();
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      onPress={() => navigation.goBack()}
      css={`
        align-items: center;
        justify-content: center;
        padding-left: ${rpx(12)}px;
        padding-right: ${rpx(8)}px;
      `}
      style={style}
    >
      <Icon
        name="chevron-left"
        color={theme.search.criterion.backButtonTextColor}
        size={rpx(36)}
      />
    </TouchableOpacity>
  );
});
